import {
  CUSTOM_TERM_FREQUENCY,
  PaymentCollectionType
} from '@features/group-management/constants';
import {
  getTermDetails,
  getTermsListFromConfig
} from '@features/group-management/helper';
import { getTermEndDate } from '@features/group-management/helpers/customTerm';
import { FormDataType, TermsList } from '@types';
import { formatISODate } from '@utils/date';

import { DEFAULT_COUNSELLOR_CUSTOM_FIELD } from './constants/common';
import { BranchMember, PaymentSchedule } from './types/request';

export const getDefaultBranchMember = (
  leadName: string,
  leadMobileNumber: string,
  leadCountryCode: string
): BranchMember => ({
  id: '',
  name: leadName,
  email: null,
  mobile: leadMobileNumber,
  country_code: leadCountryCode.replace('+', ''),
  alternate_mobile: null,
  secondary_country_code: null,
  dob: null,
  guardian_name: null,
  admission_number: null,
  admission_date: null,
  address_line_1: null,
  address_line_2: null,
  city: null,
  state: null,
  country: null,
  pincode: null,
  custom_fields: [DEFAULT_COUNSELLOR_CUSTOM_FIELD]
});

export const getDefaultGroupMemberBody = (
  leadName: string,
  leadMobileNumber: string,
  leadCountryCode: string,
  amount: string,
  paymentSchedule?: PaymentSchedule
) => ({
  name: leadName,
  mobile: leadMobileNumber,
  country_code: leadCountryCode.replace('+', ''),
  alternate_mobile: '',
  secondary_country_code: leadCountryCode.replace('+', ''),
  email: '',
  amount,
  amount_paid_till_date: 0,
  total_amount: 0,
  admission_number: null,
  admission_date: null,
  date_of_birth: null,
  guardian_name: null,
  age: null,
  address_line_1: null,
  address_line_2: null,
  city: null,
  state: null,
  country: null,
  pincode: null,
  custom_fields: [],
  ...(paymentSchedule && { payment_schedule: paymentSchedule })
});

export const getActivationDateFromSchedule = (
  paymentSchedule: any,
  nextDueAt?: string
): string => {
  if (!paymentSchedule) return '';

  if (paymentSchedule.interval === 'once') {
    const dueDateSource = paymentSchedule.next_due_at || nextDueAt;
    if (!dueDateSource) return '';
    return formatISODate({
      date: dueDateSource,
      separator: '-',
      format: 'yyyy-mm-dd'
    });
  }

  if (paymentSchedule.interval === 'monthly') {
    const dayOption = paymentSchedule.monthly_config?.day_option;
    if (dayOption === 'first_day' || dayOption === 'last_day') return dayOption;
    return paymentSchedule.monthly_config?.custom_day?.toString() || '';
  }

  if (paymentSchedule.interval === 'weekly') {
    return paymentSchedule.weekly_config?.day_of_week || '';
  }

  return '';
};

export const buildEditFormData = (
  groupName: string,
  paymentSchedule: any,
  groupMember: any
): FormDataType => {
  const isCustomTerm = paymentSchedule?.interval === 'custom_term';
  const convertedTermsList = isCustomTerm
    ? getTermsListFromConfig({
        startDate: paymentSchedule?.start_date || '',
        termsList: paymentSchedule?.term_details || []
      })
    : [];

  return {
    groupPhoto: { imageUrl: '' },
    groupName,
    activationDate: getActivationDateFromSchedule(
      paymentSchedule,
      groupMember?.next_due_at
    ),
    intervalFrequency: paymentSchedule?.interval_frequency?.toString() || '1',
    amount: groupMember?.amount || '',
    paymentInstallment: false,
    paymentCollectionType: paymentSchedule?.interval || 'monthly',
    endDate: paymentSchedule?.end_date || '',
    startDate: paymentSchedule?.start_date || '',
    isCustomTerm,
    isImmediatePayment:
      paymentSchedule?.interval === 'once' &&
      paymentSchedule?.interval_execution_type === 'immediate',
    isCollectionStarted: groupMember?.is_collection_started || false,
    termsList: convertedTermsList,
    feeCategoryEnabled:
      !!groupMember?.fee_amount_configuration?.length ||
      convertedTermsList.some(
        (t: { feeCategoryData?: any[] }) => t.feeCategoryData?.length
      )
  };
};

export const getDayOption = (activationDate: string) => {
  if (activationDate === '1' || activationDate === 'first_day')
    return 'first_day';
  if (activationDate === 'last_day') return 'last_day';
  return 'custom';
};

export const formatDateToISO = (date: string) => {
  if (!date) return null;
  return formatISODate({
    date: new Date(date),
    separator: '-',
    format: 'yyyy-mm-dd'
  });
};

export const buildPaymentSchedule = (
  groupFormData: FormDataType | null,
  startDate: string
) => {
  if (!groupFormData) return undefined;
  if (groupFormData.paymentCollectionType === 'once') return undefined;

  const getIntervalValue = () => {
    if (groupFormData.paymentCollectionType === PaymentCollectionType.WEEKLY)
      return PaymentCollectionType.WEEKLY;
    if (groupFormData.isCustomTerm) return PaymentCollectionType.CUSTOM_TERM;
    return PaymentCollectionType.MONTHLY;
  };

  const dayOption = getDayOption(groupFormData.activationDate || '');
  const customDay =
    dayOption === 'custom'
      ? Number(groupFormData.activationDate) || undefined
      : undefined;

  return {
    start_date: formatDateToISO(startDate) || null,
    end_date: formatDateToISO(groupFormData.endDate || '') || null,
    interval: getIntervalValue(),
    interval_frequency: Number(groupFormData.intervalFrequency) || 1,
    ...(getIntervalValue() === PaymentCollectionType.MONTHLY && {
      monthly_config: {
        day_option: dayOption,
        ...(customDay && { custom_day: customDay })
      }
    }),
    ...(getIntervalValue() === PaymentCollectionType.WEEKLY && {
      weekly_config: {
        day_of_week: groupFormData.activationDate
      }
    }),
    ...(groupFormData.isCustomTerm &&
      groupFormData.termsList && {
        term_details: getTermDetails(
          groupFormData.termsList,
          !!groupFormData.feeCategoryEnabled
        )
      })
  };
};

export const getFrequencyValue = (
  groupFrequency: string,
  groupIntervalFrequency?: number
) => {
  if (groupFrequency === 'monthly') {
    if (groupIntervalFrequency === 1) return 'monthly';
    return `${groupIntervalFrequency} months`;
  }
  if (groupFrequency === PaymentCollectionType.CUSTOM_TERM) {
    return CUSTOM_TERM_FREQUENCY;
  }
  return groupFrequency;
};

export const getResponseValidationError = (resData: any): string | null => {
  if (resData?.status === 'validation errors' || resData?.status === 'error') {
    const members =
      resData?.data?.members_added?.members ||
      resData?.data?.branch_members?.members ||
      [];
    const firstError = members?.[0]?.errors?.[0];
    if (firstError) return firstError.message;
    return 'Validation error occurred';
  }
  return null;
};

export const processTermsList = (
  termsList: TermsList[],
  startDate: string,
  isEditMode: boolean
): { updatedTermsList: TermsList[]; totalAmount: number } => {
  let totalAmount = 0;
  const defaultStartDate = startDate ? new Date(startDate) : new Date();
  let prevTermEndDate = new Date(
    defaultStartDate.setDate(defaultStartDate.getDate() - 1)
  ).toString();

  const updatedTermsList = termsList.map((term) => {
    let { termStartDate, termEndDate, dueDate } = term;
    const { dueAmount, termDuration } = term;
    totalAmount += Number(dueAmount);

    const prevTermEndDateAsDate = new Date(prevTermEndDate);
    termStartDate =
      termStartDate ||
      new Date(
        prevTermEndDateAsDate.setDate(prevTermEndDateAsDate.getDate() + 1)
      ).toString();
    termEndDate =
      termEndDate || getTermEndDate(termStartDate, Number(termDuration));
    prevTermEndDate = termEndDate;

    if (
      !isEditMode &&
      dueDate &&
      (new Date(dueDate) > new Date(termEndDate) ||
        new Date(dueDate) < new Date(termStartDate))
    )
      dueDate = termStartDate;

    return { ...term, termStartDate, termEndDate, dueDate };
  });

  return { updatedTermsList, totalAmount };
};
